/**
 * Scheduled Reports Page
 * Manage scheduled report delivery and view execution logs
 */

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Plus,
  Trash2,
  Loader2,
  Play,
  Clock,
  ChevronDown,
  ChevronRight,
  CalendarClock,
  CheckCircle,
  XCircle,
} from 'lucide-react';

// --- Types ---

interface ScheduledReport {
  id: string;
  name: string;
  reportId: string;
  reportName: string;
  scheduleTime: string;
  recipientEmails: string[];
  isEnabled: boolean;
  lastRunStatus: string | null;
  lastRunAt: string | null;
  createdAt: string;
}

interface ScheduledReportLog {
  id: string;
  status: string;
  message: string | null;
  rowCount: number | null;
  createdAt: string;
}

interface SavedReport {
  id: string;
  name: string;
}

// --- Constants ---

const RUN_STATUS_COLORS: Record<string, string> = {
  success: 'bg-green-100 text-green-800',
  failed: 'bg-red-100 text-red-800',
  running: 'bg-blue-100 text-blue-800',
};

// --- Component ---

export function ScheduledReportsPage() {
  const queryClient = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  // Form state
  const [formName, setFormName] = useState('');
  const [formReportId, setFormReportId] = useState('');
  const [formTime, setFormTime] = useState('08:00');
  const [formEmails, setFormEmails] = useState('');

  // Fetch scheduled reports
  const { data, isLoading } = useQuery({
    queryKey: ['scheduledReports'],
    queryFn: () => api.getScheduledReports(),
  });
  const scheduledReports = (data?.data || []) as ScheduledReport[];

  // Fetch saved reports for the dropdown
  const { data: savedData } = useQuery({
    queryKey: ['savedReports'],
    queryFn: () => api.getSavedReports(),
  });
  const savedReports = (savedData?.data || []) as SavedReport[];

  // Create mutation
  const createMutation = useMutation({
    mutationFn: (payload: Record<string, unknown>) => api.createScheduledReport(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scheduledReports'] });
      setDialogOpen(false);
      resetForm();
    },
  });

  // Update mutation (for enable/disable toggle)
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) =>
      api.updateScheduledReport(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scheduledReports'] });
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.deleteScheduledReport(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scheduledReports'] });
    },
  });

  // Run now mutation
  const runNowMutation = useMutation({
    mutationFn: (id: string) => api.runScheduledReportNow(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scheduledReports'] });
      queryClient.invalidateQueries({ queryKey: ['scheduledReportLogs'] });
    },
  });

  function resetForm() {
    setFormName('');
    setFormReportId('');
    setFormTime('08:00');
    setFormEmails('');
  }

  function handleCreate() {
    const emails = formEmails
      .split('\n')
      .map((e) => e.trim())
      .filter(Boolean);
    createMutation.mutate({
      name: formName,
      reportId: formReportId,
      scheduleTime: formTime,
      recipientEmails: emails,
    });
  }

  function handleToggleEnabled(report: ScheduledReport) {
    updateMutation.mutate({
      id: report.id,
      data: { isEnabled: !report.isEnabled },
    });
  }

  function toggleExpanded(id: string) {
    setExpandedId((prev) => (prev === id ? null : id));
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Scheduled Reports</h1>
          <p className="text-muted-foreground">
            Configure automatic report delivery on a schedule
          </p>
        </div>
        <Button onClick={() => setDialogOpen(true)}>
          <Plus className="h-4 w-4 mr-2" />
          New Schedule
        </Button>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin" />
        </div>
      ) : scheduledReports.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <CalendarClock className="h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-muted-foreground">No scheduled reports configured</p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-8" />
                  <TableHead>Name</TableHead>
                  <TableHead>Report</TableHead>
                  <TableHead>Schedule</TableHead>
                  <TableHead className="text-center">Enabled</TableHead>
                  <TableHead className="text-center">Last Run Status</TableHead>
                  <TableHead>Last Run</TableHead>
                  <TableHead className="w-32">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {scheduledReports.map((report) => (
                  <>
                    <TableRow key={report.id}>
                      <TableCell>
                        <button onClick={() => toggleExpanded(report.id)}>
                          {expandedId === report.id ? (
                            <ChevronDown className="h-4 w-4" />
                          ) : (
                            <ChevronRight className="h-4 w-4" />
                          )}
                        </button>
                      </TableCell>
                      <TableCell className="font-medium">{report.name}</TableCell>
                      <TableCell>{report.reportName}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1 text-sm">
                          <Clock className="h-3 w-3 text-muted-foreground" />
                          {report.scheduleTime}
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        <button
                          onClick={() => handleToggleEnabled(report)}
                          disabled={updateMutation.isPending}
                          className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                            report.isEnabled
                              ? 'bg-green-100 text-green-800 hover:bg-green-200'
                              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                          }`}
                        >
                          {report.isEnabled ? 'Enabled' : 'Disabled'}
                        </button>
                      </TableCell>
                      <TableCell className="text-center">
                        {report.lastRunStatus ? (
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              RUN_STATUS_COLORS[report.lastRunStatus] ||
                              'bg-gray-100 text-gray-800'
                            }`}
                          >
                            {report.lastRunStatus}
                          </span>
                        ) : (
                          <span className="text-sm text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      <TableCell className="text-sm">
                        {report.lastRunAt
                          ? new Date(report.lastRunAt).toLocaleString()
                          : '-'}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => runNowMutation.mutate(report.id)}
                            disabled={runNowMutation.isPending}
                            title="Run Now"
                          >
                            <Play className="h-3 w-3" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              if (confirm('Delete this scheduled report?'))
                                deleteMutation.mutate(report.id);
                            }}
                          >
                            <Trash2 className="h-3 w-3 text-destructive" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                    {expandedId === report.id && (
                      <TableRow key={`${report.id}-logs`}>
                        <TableCell colSpan={8} className="bg-muted/30 p-0">
                          <ExecutionLogs reportId={report.id} />
                        </TableCell>
                      </TableRow>
                    )}
                  </>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* New Schedule Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>New Scheduled Report</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Schedule Name</Label>
              <Input
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
                placeholder="e.g., Daily Invoice Summary"
              />
            </div>
            <div>
              <Label>Saved Report</Label>
              <Select value={formReportId} onValueChange={setFormReportId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a saved report" />
                </SelectTrigger>
                <SelectContent>
                  {savedReports.map((r) => (
                    <SelectItem key={r.id} value={r.id}>
                      {r.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {savedReports.length === 0 && (
                <p className="text-xs text-muted-foreground mt-1">
                  No saved reports available. Save a report in the Report Builder first.
                </p>
              )}
            </div>
            <div>
              <Label>Schedule Time (HH:MM)</Label>
              <Input
                type="time"
                value={formTime}
                onChange={(e) => setFormTime(e.target.value)}
              />
            </div>
            <div>
              <Label>Recipient Emails (one per line)</Label>
              <Textarea
                rows={3}
                value={formEmails}
                onChange={(e) => setFormEmails(e.target.value)}
                placeholder={"user@example.com\nmanager@example.com"}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleCreate}
              disabled={!formName || !formReportId || createMutation.isPending}
            >
              {createMutation.isPending && (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              )}
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// --- Execution Logs Sub-component ---

function ExecutionLogs({ reportId }: { reportId: string }) {
  const { data, isLoading } = useQuery({
    queryKey: ['scheduledReportLogs', reportId],
    queryFn: () => api.getScheduledReportLogs(reportId),
  });
  const logs = (data?.data || []) as ScheduledReportLog[];

  if (isLoading) {
    return (
      <div className="flex justify-center py-6">
        <Loader2 className="h-4 w-4 animate-spin" />
      </div>
    );
  }

  if (logs.length === 0) {
    return (
      <div className="py-6 text-center text-sm text-muted-foreground">
        No execution logs yet
      </div>
    );
  }

  return (
    <div className="p-4">
      <h4 className="text-sm font-semibold mb-3">Recent Execution Logs</h4>
      <div className="space-y-2">
        {logs.map((log) => (
          <div
            key={log.id}
            className="flex items-start gap-3 text-sm border rounded-md p-3"
          >
            {log.status === 'success' ? (
              <CheckCircle className="h-4 w-4 text-green-500 mt-0.5" />
            ) : (
              <XCircle className="h-4 w-4 text-red-500 mt-0.5" />
            )}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <Badge
                  variant={log.status === 'success' ? 'default' : 'destructive'}
                  className="text-xs"
                >
                  {log.status}
                </Badge>
                {log.rowCount !== null && (
                  <span className="text-xs text-muted-foreground">
                    {log.rowCount} rows
                  </span>
                )}
              </div>
              {log.message && (
                <p className="text-muted-foreground mt-1 truncate">{log.message}</p>
              )}
              <p className="text-xs text-muted-foreground mt-1">
                {new Date(log.createdAt).toLocaleString()}
              </p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
